import React, { ChangeEvent } from "react";
import { generateClient, GraphQLResult } from "aws-amplify/data";
import { fetchAuthSession } from "aws-amplify/auth";
import DatePicker from "react-datepicker";
import moment from "moment-timezone";
import "react-datepicker/dist/react-datepicker.css";
import {
  IPaymentV2ErrorCountResponse,
  IProcessRetryExceededPaymentV2MutationResponse,
  IRetryExceededPaymentV2CountQueryResponse,
  maintenanceWindows,
  processRetryExceededPaymentV2Mutation,
  ProviderDrMode,
  retryExceededPaymentV2CountQuery,
  updateMaintenanceWindow,
} from "lib";
import styled from "styled-components";
import { withSnackbar, ProviderContext } from "notistack";
import Settings from "shared/settings";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import "react-tabs/style/react-tabs.css";
import { PaymentV2Details } from "./pages/paymentV2Details";
import DisplayCard from "./shared/displayCard";
import { SummaryContainer } from "./shared/summaryContainer";
import {
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
} from "@material-ui/core";
import { StyledSwitch } from "./components";
import { getProviderDrMode, updateProviderDrMode } from "lib/api/drMode";
import { QAAuth } from "pages/QAAuth";
import EnvironmentMapping from "components/EnvironmentMapping";
import UserConfiguration from "components/UserConfiguration";
import SubscriptionErrorTable from "components/SubscriptionErrorTable";
import SubhubKnowledgeBase from "components/SubhubKnowledgeBase";

const StyledTabPanel = styled(TabPanel)`
  padding-left: 30px;
  ol {
    padding-inline-start: 20px;
  }
`;
const StyledMainContainer = styled.div`
  padding: 20px;
  .react-tabs__tab--selected {
    background-color: gray;
  }
`;

const styles = {
  container: {
    padding: "20px",
  },
  button: {
    backgroundColor: "black",
    color: "white",
    outline: "none",
    fontSize: 18,
    padding: "12px 12px",
    margin: "12px 0px",
    borderStyle: "none",
    cursor: "unset",
  },
  disabledButton: {
    backgroundColor: "darkgray",
    color: "white",
    outline: "none",
    fontSize: 18,
    padding: "12px 12px",
    margin: "12px 0px",
    borderStyle: "none",
    cursor: "not-allowed",
  },
  value: {
    fontSize: 18,
    fontWeight: 600,
  },
};

/**
 * Maintenance window entry type.
 * Represents an entry in the global Hyperion configuration's maintenance window array.
 */
type MaintenanceWindow = {
  startDatetime: string;
  endDatetime: string;
  maintenanceMessage: string;
};

type cognitoGroups = {
  qa: boolean;
  admin: boolean;
  ops: boolean;
};
interface IAppStateType {
  prd: any;
  pp: any;
  groups: cognitoGroups;
  others: any;
  maintenanceWindows: MaintenanceWindow[] | undefined;
  newMaintenanceWindow: {
    start: Date;
    end: Date;
    message: string;
  };
  paymentV2Errors: IPaymentV2ErrorCountResponse | undefined;
  paymentV2RetryEnabled: boolean;
  drProviderIds: string[];
  drEnabled: boolean;
}

const DrModeEnabledProviders = [
  { displayName: "Optus Sport", providerId: "provider-optussport" },
  { displayName: "Netflix", providerId: "netflix-optus.au" },
];

class App extends React.Component<ProviderContext, IAppStateType> {
  refreshInterval: any;
  constructor(props: ProviderContext) {
    super(props);

    this.state = {
      prd: {
        username: "",
        password: "",
      },
      groups: {
        qa: false,
        admin: false,
        ops: false,
      },
      pp: {
        username: "",
        password: "",
      },
      others: {
        username: "",
        password: "",
      },
      maintenanceWindows: undefined,
      newMaintenanceWindow: {
        start: new Date(),
        end: moment().add(1, "day").toDate(),
        message: "Enter the updated maintenance message here.",
      },
      paymentV2Errors: undefined,
      paymentV2RetryEnabled: false,
      drProviderIds: [],
      drEnabled: false,
    };
    this.getMaintenanceWindows = this.getMaintenanceWindows.bind(this);
    this.setMaintenanceWindowStart = this.setMaintenanceWindowStart.bind(this);
    this.setMaintenanceWindowEnd = this.setMaintenanceWindowEnd.bind(this);
    this.setMaintenanceWindowMessage =
      this.setMaintenanceWindowMessage.bind(this);
    this.updateMaintenanceMessage = this.updateMaintenanceMessage.bind(this);
    this.fetchPaymentV2Errors = this.fetchPaymentV2Errors.bind(this);
    this.retryPaymentV2Errors = this.retryPaymentV2Errors.bind(this);
    this.toggleDrMode = this.toggleDrMode.bind(this);
    this.refreshInterval = null;
  }

  componentDidMount() {
    // Settings.appSecretsViewEnabled && this.fetchSecrets();
    this.getGroups().then(() => {
      this.fetchPaymentV2Errors();
      this.getMaintenanceWindows();
      this.updateDrMode();
    });

    this.refreshInterval = setInterval(() => {
      this.fetchPaymentV2Errors();
    }, 30000);
  }

  componentWillUnmount() {
    clearInterval(this.refreshInterval);
  }

  async fetchPaymentV2Errors() {
    this.props.enqueueSnackbar("Fetching PaymentV2 error count...");

    if (this.state.groups && this.state.groups.qa) {
      return;
    }

    try {
      const client = generateClient({
        authMode: "identityPool",
      });
      const res = (await client.graphql({
        query: retryExceededPaymentV2CountQuery,
        authMode: "identityPool",
      })) as GraphQLResult<IRetryExceededPaymentV2CountQueryResponse>;

      if (!res || !res.data) {
        throw new Error("Invalid response while getting PaymentV2 error count");
      }

      const paymentV2Errors = res.data.retryExceededPaymentV2Count;
      const totalCount =
        paymentV2Errors.maxRetryExceededCount +
        paymentV2Errors.otherErrors.reduce((p, c) => p + c.count, 0);
      const paymentV2RetryEnabled = totalCount > 0;
      this.setState({ paymentV2Errors, paymentV2RetryEnabled });
    } catch (err: any) {
      console.log("Error while fetching PaymentV2 error count", err);
      const message =
        err.errors && err.errors.length
          ? err.errors[0].message
          : "Error while fetching PaymentV2 error count";
      this.props.enqueueSnackbar(message, { variant: "error" });
    }
  }

  /**
   * Fetches the existing maintenance windows.
   */
  async getMaintenanceWindows() {
    this.props.enqueueSnackbar("Fetching maintenance window...");

    if (this.state.groups && this.state.groups.qa) {
      return;
    }

    try {
      const client = generateClient({
        authMode: "identityPool",
      });
      const queryResults = (await client.graphql({
        query: maintenanceWindows,
        authMode: "identityPool",
      })) as GraphQLResult<{
        maintenanceWindows: MaintenanceWindow[];
      }>;

      this.setState({
        maintenanceWindows: queryResults.data?.maintenanceWindows,
      });
    } catch (err: any) {
      console.error(err);

      let errorMessage = "Error fetching maintenance windows";
      if (err.errors && err.errors.length) {
        errorMessage = err.errors[0].message;
      }

      this.props.enqueueSnackbar(errorMessage, { variant: "error" });
    }
  }

  async retryPaymentV2Errors() {
    this.setState({ paymentV2RetryEnabled: false });

    try {
      const client = generateClient({
        authMode: "identityPool",
      });
      const res = (await client.graphql({
        query: processRetryExceededPaymentV2Mutation,
        authMode: "identityPool",
      })) as GraphQLResult<IProcessRetryExceededPaymentV2MutationResponse>;
      console.log(`processRetryExceededPaymentV2`, res);

      const count = res.data?.processRetryExceededPaymentV2;
      if (count && count >= 0) {
        this.props.enqueueSnackbar(
          `${count} Payments have been successfully scheduled for retry`,
          { variant: "success" }
        );
      } else {
        this.props.enqueueSnackbar(`error: ${JSON.stringify(res.data)}`, {
          variant: "error",
        });
      }
    } catch (err: any) {
      console.log("Error while retrying PaymentV2 transactions", err);
      const message =
        err.errors && err.errors.length
          ? err.errors[0].message
          : "Error while retrying PaymentV2 transactions";
      this.props.enqueueSnackbar(message, { variant: "error" });
    }

    await this.fetchPaymentV2Errors();
  }

  renderPaymentRetryControls() {
    return (
      <>
        <SummaryContainer>
          <DisplayCard
            header="Total Payment in QUEUE"
            count={this.state.paymentV2Errors?.queueTotalCount || 0}
          />
          <DisplayCard
            header="Total Failed Payment"
            count={this.state.paymentV2Errors?.maxRetryExceededCount || 0}
          />
          <DisplayCard
            header="Payment in QUEUE > 48 hours"
            count={this.state.paymentV2Errors?.queueMoreThan48HoursCount || 0}
          />
        </SummaryContainer>
        <br />
        <button
          disabled={!this.state.paymentV2RetryEnabled}
          style={
            this.state.paymentV2RetryEnabled
              ? styles.button
              : styles.disabledButton
          }
          onClick={() => this.retryPaymentV2Errors.call(this)}
        >
          Schedule V2 Again
        </button>
        <ol>
          {Array.isArray(this.state.paymentV2Errors?.otherErrors) &&
            this.state.paymentV2Errors!.otherErrors.map((err) => (
              <li>
                <span style={styles.value}>ErrorCode:</span> {err.errorCode}{" "}
                <br />
                <span style={styles.value}>Count:</span> {err.count} <br />
                <span style={styles.value}>ErrorMessages:</span>
                <ol type="a">
                  {Array.isArray(err.errorMessages) &&
                    err.errorMessages.map((m) => <li>{m}</li>)}
                </ol>
              </li>
            ))}
        </ol>
      </>
    );
  }

  /**
   * Renders the maintenance window controls.
   */
  renderMaintenanceWindowControls() {
    const { maintenanceWindows, newMaintenanceWindow } = this.state;

    return (
      <>
        <h1>Scheduled Maintenance Window</h1>
        <h2>Current Maintenance Window</h2>
        {maintenanceWindows && maintenanceWindows.length && (
          <div>
            <div>
              <b>Start: </b>
              <span>
                {moment(maintenanceWindows[0].startDatetime)
                  .tz("Australia/Sydney")
                  .toString()}
              </span>
            </div>
            <div>
              <b>End: </b>
              <span>
                {moment(maintenanceWindows[0].endDatetime)
                  .tz("Australia/Sydney")
                  .toString()}
              </span>
            </div>
            <div>
              <b>Message: </b>
              <span>{maintenanceWindows[0].maintenanceMessage}</span>
            </div>
          </div>
        )}
        {(!maintenanceWindows || !maintenanceWindows.length) && (
          <div>No maintenance window is currently defined.</div>
        )}
        <h2>Update Maintenance Window</h2>
        <div>
          <h3>Start</h3>
          <DatePicker
            selected={newMaintenanceWindow.start}
            onChange={this.setMaintenanceWindowStart}
            showTimeSelect
            dateFormat="yyyy-MM-dd h:mm aa"
          />
        </div>
        <div>
          <h3>End</h3>
          <DatePicker
            selected={newMaintenanceWindow.end}
            onChange={this.setMaintenanceWindowEnd}
            showTimeSelect
            dateFormat="yyyy-MM-dd h:mm aa"
          />
        </div>
        <div>
          <h3>Message</h3>
          <textarea
            onChange={this.setMaintenanceWindowMessage}
            value={newMaintenanceWindow.message}
            style={{ width: "70%" }}
          />
        </div>
        <div>
          <button onClick={this.updateMaintenanceMessage}>Update</button>
        </div>
      </>
    );
  }

  /**
   * Sets the updated maintenance window end date.
   * @param {Date} date The new end date for the maintenance window.
   */
  setMaintenanceWindowEnd(date: Date) {
    const { newMaintenanceWindow } = this.state;
    newMaintenanceWindow.end = date;

    this.setState({
      newMaintenanceWindow,
    });
  }

  /**
   * Sets the updated maintenance window message.
   * @param {ChangeEvent} event The triggering HTML element change event.
   */
  setMaintenanceWindowMessage(event: ChangeEvent<HTMLTextAreaElement>) {
    const { newMaintenanceWindow } = this.state;
    if (event.target.value) {
      newMaintenanceWindow.message = event.target.value;
    }

    this.setState({
      newMaintenanceWindow,
    });
  }

  /**
   * Sets the updated maintenance window start date.
   * @param {Date} date The new start date for the maintenance window.
   */
  setMaintenanceWindowStart(date: Date) {
    const { newMaintenanceWindow } = this.state;
    newMaintenanceWindow.start = date;

    this.setState({
      newMaintenanceWindow,
    });
  }

  /**
   * Sends the request to update the maintenance window.
   */
  async updateMaintenanceMessage() {
    const { newMaintenanceWindow } = this.state;

    try {
      const client = generateClient({
        authMode: "identityPool",
      });
      const queryResults = (await client.graphql({
        query: updateMaintenanceWindow,
        authMode: "identityPool",
        variables: {
          startDatetime: moment(newMaintenanceWindow.start)
            .tz("Australia/Sydney")
            .toISOString(),
          endDatetime: moment(newMaintenanceWindow.end)
            .tz("Australia/Sydney")
            .toISOString(),
          maintenanceMessage: newMaintenanceWindow.message,
        },
      })) as GraphQLResult<{
        maintenanceWindows: MaintenanceWindow[];
      }>;

      this.setState({
        maintenanceWindows: queryResults.data?.maintenanceWindows,
      });
    } catch (err: any) {
      console.error(err);

      let errorMessage = "Error fetching maintenance windows";
      if (err.errors && err.errors.length) {
        errorMessage = err.errors[0].message;
      }

      this.props.enqueueSnackbar(errorMessage, { variant: "error" });
    }
  }

  updateDrMode(modes?: ProviderDrMode[]) {
    if (this.state.groups && this.state.groups.qa) {
      return;
    }
    if (!modes) {
      getProviderDrMode().then((modes) => {
        if (!modes) {
          this.props.enqueueSnackbar(
            `Error while getting DR mode for providers`,
            {
              variant: "error",
            }
          );
          return;
        }
        this.updateDrMode(modes);
      });
      return;
    }

    const drProviderIds = modes.map((m) => m.providerId);
    this.setState({ drProviderIds, drEnabled: true });
  }

  async toggleDrMode(providerId: string) {
    this.setState({ drEnabled: false });
    let drMode = !this.state.drProviderIds.includes(providerId);
    const modes = await updateProviderDrMode({ drMode, providerId });

    if (!modes) {
      this.props.enqueueSnackbar(
        `Error while setting DR mode for ${providerId}`,
        {
          variant: "error",
        }
      );
      return;
    }

    this.updateDrMode(modes);
  }

  renderDrControls() {
    return (
      <FormControl component="fieldset" disabled={!this.state.drEnabled}>
        <FormLabel>Subhub Bypass Mode</FormLabel>
        <FormGroup row>
          {DrModeEnabledProviders.map((element) => {
            return (
              <FormControlLabel
                key={element.providerId}
                value={element.providerId}
                label={element.displayName}
                labelPlacement="start"
                control={
                  <StyledSwitch
                    color="default"
                    checked={this.state.drProviderIds.includes(
                      element.providerId
                    )}
                    onChange={(e) => this.toggleDrMode(element.providerId)}
                  />
                }
              />
            );
          })}
        </FormGroup>
      </FormControl>
    );
  }
  async getGroups() {
    let groupValues: cognitoGroups = {
      qa: false,
      admin: false,
      ops: false,
    };

    try {
      const { tokens } = await fetchAuthSession();
      const userGroups: string[] = tokens?.accessToken?.payload[
        "cognito:groups"
      ] as string[];
      if (!userGroups) {
        this.setState({ groups: groupValues });
        return;
      }
      if (userGroups.some((group: string) => group === "QA")) {
        groupValues.qa = true;
      }
      if (userGroups.some((group: string) => group === "Admins")) {
        groupValues.admin = true;
      }
      if (userGroups.some((group: string) => group === "Ops")) {
        groupValues.ops = true;
      }
      this.setState({ groups: groupValues });
      return;
    } catch (error) {
      console.error("Fetching current authenticated user error:", error);
      this.setState({ groups: groupValues });
    }
  }

  render() {
    const tabs = [];
    const tabPanels = [];

    if (this.state.groups.ops || this.state.groups.admin) {
      tabs.push([<Tab>Overview</Tab>, <Tab>Payment V2 details</Tab>]);
      tabPanels.push([
        <StyledTabPanel>
          {Settings.drModeEnabled && this.renderDrControls()}
          {this.renderPaymentRetryControls()}
          {this.renderMaintenanceWindowControls()}
        </StyledTabPanel>,
        <TabPanel>
          <PaymentV2Details />
        </TabPanel>,
      ]);
    }

    if (this.state.groups.qa || this.state.groups.admin) {
      tabs.push(<Tab>QA Credentials</Tab>);
      tabPanels.push(
        <TabPanel>
          <QAAuth />
          {process.env.REACT_APP_AMPLIFY_ENV !== "prd" && (
            <>
              <EnvironmentMapping />
              <UserConfiguration />
            </>
          )}
        </TabPanel>
      );
    }

    if (
      this.state.groups.ops ||
      this.state.groups.admin ||
      this.state.groups.qa
    ) {
      tabs.push(<Tab>Subhub KnowledgeBase</Tab>);
      tabPanels.push(
        <TabPanel>
          <SubhubKnowledgeBase />
        </TabPanel>
      );
    }

    if (this.state.groups.admin) {
      tabs.push(<Tab>SubscriptionErrorTable</Tab>);
      tabPanels.push(
        <TabPanel>
          <SubscriptionErrorTable />
        </TabPanel>
      );
    }

    return (
      <StyledMainContainer>
        <Tabs>
          {tabs.length > 0 && tabPanels.length > 0 ? (
            <>
              <TabList>{tabs}</TabList>
              {tabPanels}
            </>
          ) : (
            "You have no associated groups in Cognito!"
          )}
        </Tabs>
      </StyledMainContainer>
    );
  }
}

export default withSnackbar(App);
